/**
 * MEVIA - Admin Dashboard with 3 Tabs
 * Tab 1: Cultura (Dimensions) - Default
 * Tab 2: Personalidad (Enneagram)
 * Tab 3: Reporte Integral (Combined)
 */

import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Target, 
  Users, 
  UsersRound,
  Building,
  Building2,
  BarChart3,
  Globe,
  Settings,
  LogOut,
  Copy,
  RefreshCw,
  FileText,
  Download,
  ChevronDown,
  ClipboardList,
  CreditCard,
  Lock,
  Network,
  Clock,
  X as XIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import NotificationBell from './NotificationBell';
import NOM035Notifications from './NOM035Notifications';
import CultureTab from './tabs/CultureTab';
import PersonalityTab from './tabs/PersonalityTab';
import IntegratedReportTab from './tabs/IntegratedReportTab';
import ActionPlanDashboard from './ActionPlanDashboard';
import StrategicPlanView from './StrategicPlanView';
import DashboardCentral from './DashboardCentral';
import PeopleAnalytics from './PeopleAnalytics';
import NOM035Panel from './NOM035Panel';
import NOM035CentroSelector from './NOM035CentroSelector';
import RazonesSocialesAdmin from './RazonesSocialesAdmin';
import PulsoDashboard from './PulsoDashboard';
import ViewSwitcher from './ViewSwitcher';
import PendingTasksBanner from './PendingTasksBanner';
import axios from 'axios';
import logger from '../utils/logger';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const { language, changeLanguage, t } = useLanguage();
  const { hasAccess, planName, hasActiveSubscription } = useSubscription();
  const [activeTab, setActiveTab] = useState('dashboard_central');
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingOptional, setPendingOptional] = useState([]);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Handle URL tab parameter
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (user?.rol === 'company_admin') {
      loadCompanyInfo();
      loadPendingOptional();
    }
  }, [user]);

  const loadPendingOptional = async () => {
    try {
      if (!user?.empresa_id) return;
      const res = await axios.get(`/v5/empresa/${user.empresa_id}/onboarding/pending-optional`);
      if (res.data.success && res.data.data.has_pending) {
        setPendingOptional(res.data.data.pending_steps);
      }
    } catch (e) { /* silent */ }
  };

  const dismissBanner = async () => {
    setBannerDismissed(true);
    try {
      await axios.post(`/v5/empresa/${user.empresa_id}/onboarding/dismiss-banner`);
    } catch (e) { /* silent */ }
  };

  const loadCompanyInfo = async () => {
    try {
      const response = await axios.get('/companies/my-company');
      if (response.data.success) {
        setCompanyInfo(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading company info:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyCompanyCode = () => {
    if (companyInfo?.codigo_acceso) {
      navigator.clipboard.writeText(companyInfo.codigo_acceso);
      toast.success(language === 'es' ? 'Código copiado' : 'Code copied');
    }
  };

  // M-01: NOM-035 centro de trabajo selector state
  const [nom035Centro, setNom035Centro] = useState(null);

  // Tab configuration with permission mapping
  // Note: Periodos de Evaluación and Identidad Corporativa moved to Settings panel
  const tabConfig = [
    { 
      key: 'dashboard_central', 
      label: { es: 'Dashboard Central', en: 'Central Dashboard' }, 
      icon: '📈',
      permission: 'dashboard_central'
    },
    { 
      key: 'cultura', 
      label: { es: 'Cultura', en: 'Culture' }, 
      icon: '🎯',
      permission: 'culture'
    },
    { 
      key: 'personalidad', 
      label: { es: 'Personalidad', en: 'Personality' }, 
      icon: '🎭',
      permission: 'personality'
    },
    { 
      key: 'integral', 
      label: { es: 'Reporte Integral', en: 'Integrated Report' }, 
      icon: '📊',
      permission: 'integrated_report'
    },
    { 
      key: 'nom035', 
      label: { es: 'NOM-035', en: 'NOM-035' }, 
      icon: '📋',
      permission: 'nom035'
    },
    { 
      key: 'action_plans', 
      label: { es: 'Planes de Acción', en: 'Action Plans' }, 
      icon: '📋',
      permission: 'action_plans'
    },
    { 
      key: 'strategic_plans', 
      label: { es: 'Plan Estratégico', en: 'Strategic Plan' }, 
      icon: '🎯',
      permission: 'strategic_plans'
    },
    { 
      key: 'people_analytics', 
      label: { es: 'People Analytics', en: 'People Analytics' }, 
      icon: '📊',
      permission: 'people_analytics'
    },
    { 
      key: 'pulso_mevia', 
      label: { es: 'Pulso MEVIA', en: 'MEVIA Pulse' }, 
      icon: '💓',
      permission: 'pulse_surveys'
    },
    { 
      key: 'organizacion', 
      label: { es: 'Organización', en: 'Organization' }, 
      icon: '🏢',
      permission: 'culture'
    }
  ];

  // Filter tabs based on permissions
  const availableTabs = tabConfig.filter(tab => hasAccess(tab.permission));

  // If current tab is not available, switch to first available
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find(t => t.key === activeTab)) {
      setActiveTab(availableTabs[0].key);
    }
  }, [availableTabs, activeTab]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b" style={{ borderBottomColor: '#1A878D', borderBottomWidth: '2px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img 
                src="/images/mevia-logo-horizontal.png" 
                alt="MEVIA" 
                className="h-10"
              />
              <div className="border-l pl-4" style={{ borderColor: '#E5E7EB' }}>
                <h1 className="text-xl font-bold text-gray-900">
                  {t('welcome', 'Bienvenido')}, {user?.nombre}
                </h1>
                <p className="text-sm text-gray-600">
                  {user?.empresa_nombre || t('individual_user', 'Usuario Individual')}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <NOM035Notifications onNavigate={(url) => navigate(url)} />
              
              {/* M-03: View Switcher */}
              <ViewSwitcher />
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}
              >
                <Globe className="h-4 w-4 mr-2" />
                {language === 'es' ? 'EN' : 'ES'}
              </Button>
              
              {/* Admin Menu Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Settings className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Admin' : 'Admin'}
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    {language === 'es' ? 'Administración' : 'Administration'}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/employees')}>
                    <Users className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Gestionar Empleados' : 'Manage Employees'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-team')}>
                    <UsersRound className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Mi Equipo' : 'My Team'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/org-chart')}>
                    <Network className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Organigrama' : 'Org Chart'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/assessments-management')}>
                    <ClipboardList className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Gestionar Evaluaciones' : 'Manage Assessments'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/my-subscription')}>
                    <CreditCard className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Mi Suscripción' : 'My Subscription'}
                    {planName && (
                      <Badge variant="outline" className="ml-auto text-xs">
                        {planName}
                      </Badge>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/assessment-config')}>
                    <Settings className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Configuración' : 'Settings'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button variant="ghost" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* M-03 §3.4: Pending employee tasks banner */}
      <PendingTasksBanner />

      {/* Company Info Bar */}
      {companyInfo && (
        <div className="bg-gradient-to-r from-teal-50 to-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <Building className="h-5 w-5 mr-2" style={{ color: '#1A878D' }} />
                  <span className="font-medium">{companyInfo.nombre}</span>
                </div>
                <div className="flex items-center bg-white px-3 py-1 rounded-lg border">
                  <span className="text-sm text-gray-500 mr-2">
                    {language === 'es' ? 'Código:' : 'Code:'}
                  </span>
                  <span className="font-mono font-bold text-teal-600">{companyInfo.codigo_acceso}</span>
                  <Button variant="ghost" size="sm" className="ml-2 h-6 w-6 p-0" onClick={copyCompanyCode}>
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center space-x-4 text-sm">
                <span className="flex items-center">
                  <Users className="h-4 w-4 mr-1 text-gray-400" />
                  {companyInfo.total_employees || 0} {language === 'es' ? 'empleados' : 'employees'}
                </span>
                {planName && (
                  <span className="flex items-center">
                    <CreditCard className="h-4 w-4 mr-1 text-gray-400" />
                    <Badge variant="outline" className="text-xs">{planName}</Badge>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* D11: Pending Optional Steps Banner */}
      {pendingOptional.length > 0 && !bannerDismissed && (
        <div className="bg-amber-50 border-b border-amber-200" data-testid="pending-optional-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {language === 'es' ? 'Pasos opcionales pendientes de configurar' : 'Pending optional setup steps'}
                  </p>
                  <div className="flex gap-2 mt-1">
                    {pendingOptional.map(step => (
                      <Badge key={step.id} variant="outline" className="text-xs text-amber-700 border-amber-300 cursor-pointer hover:bg-amber-100"
                        onClick={() => navigate('/assessment-config?tab=onboarding')}>
                        {language === 'es' ? step.title_es : step.title_en}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={dismissBanner} className="text-amber-600 hover:text-amber-800">
                <XIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content with Tabs */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {availableTabs.length === 0 ? (
          /* No modules available */
          <Card className="border-2 border-dashed border-gray-300">
            <CardContent className="py-12 text-center">
              <Lock className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {language === 'es' ? 'Sin acceso a módulos' : 'No module access'}
              </h3>
              <p className="text-gray-600 mb-6">
                {language === 'es' 
                  ? 'Tu suscripción actual no incluye acceso a ningún módulo. Contacta al administrador para actualizar tu plan.'
                  : 'Your current subscription does not include access to any modules. Contact the administrator to upgrade your plan.'}
              </p>
              <Button variant="outline" onClick={() => navigate('/my-subscription')}>
                <CreditCard className="h-4 w-4 mr-2" />
                {language === 'es' ? 'Ver Mi Suscripción' : 'View My Subscription'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className={`grid w-full h-14 bg-white shadow-sm`} style={{ gridTemplateColumns: `repeat(${availableTabs.length}, 1fr)` }}>
              {availableTabs.map((tab) => (
                <TabsTrigger 
                  key={tab.key}
                  value={tab.key}
                  className="flex items-center gap-2 text-base data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700 data-[state=active]:border-b-2 data-[state=active]:border-teal-600"
                >
                  <span className="text-xl">{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.label[language]}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {hasAccess('dashboard_central') && (
              <TabsContent value="dashboard_central" className="space-y-6">
                <DashboardCentral empresaId={user?.empresa_id} />
              </TabsContent>
            )}

            {hasAccess('culture') && (
              <TabsContent value="cultura" className="space-y-6">
                <CultureTab companyInfo={companyInfo} />
              </TabsContent>
            )}

            {hasAccess('personality') && (
              <TabsContent value="personalidad" className="space-y-6">
                <PersonalityTab />
              </TabsContent>
            )}

            {hasAccess('integrated_report') && (
              <TabsContent value="integral" className="space-y-6">
                <IntegratedReportTab />
              </TabsContent>
            )}

            {hasAccess('culture') && (
              <TabsContent value="nom035" className="space-y-6">
                {nom035Centro ? (
                  <div>
                    <div className="flex items-center gap-2 mb-4 text-sm text-gray-500">
                      <button 
                        onClick={() => setNom035Centro(null)}
                        className="text-indigo-600 hover:underline"
                        data-testid="nom-back-selector"
                      >
                        {language === 'es' ? 'Centros de Trabajo' : 'Work Centers'}
                      </button>
                      <span>/</span>
                      <span className="font-medium text-gray-700">{nom035Centro.nombre}</span>
                    </div>
                    <NOM035Panel sucursalId={nom035Centro.id} />
                  </div>
                ) : (
                  <NOM035CentroSelector 
                    empresaId={user?.empresa_id} 
                    language={language}
                    onSelectCentro={(centro) => setNom035Centro(centro)}
                  />
                )}
              </TabsContent>
            )}

            {hasAccess('action_plans') && (
              <TabsContent value="action_plans" className="space-y-6">
                <ActionPlanDashboard />
              </TabsContent>
            )}

            {hasAccess('action_plans') && (
              <TabsContent value="strategic_plans" className="space-y-6">
                <StrategicPlanView empresaId={user?.empresa_id} />
              </TabsContent>
            )}

            {hasAccess('action_plans') && (
              <TabsContent value="people_analytics" className="space-y-6">
                <PeopleAnalytics empresaId={user?.empresa_id} />
              </TabsContent>
            )}

            {hasAccess('pulse_surveys') && (
              <TabsContent value="pulso_mevia" className="space-y-6">
                <PulsoDashboard />
              </TabsContent>
            )}

            {hasAccess('culture') && (
              <TabsContent value="organizacion" className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <RazonesSocialesAdmin empresaId={user?.empresa_id} language={language} />
                </div>
              </TabsContent>
            )}
          </Tabs>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
